import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mantine/core';
import Footer from '../../components/Footer';
import PlatformNavBar from '../../components/PlatformNavBar';
import './AiCalculator.css';

const SESSION_BLOCK_SECONDS = 120

// ─── New pricing model ────────────────────────────────────────────────
const NEW_UNIT_LIST_PRICE = 115
const NEW_SCRIPTED_MINUTES_PER_UNIT = 1600
const NEW_AUTONOMOUS_MINUTES_PER_UNIT = 250

// ─── Cisco Digital pricing model ──────────────────────────────────────
const CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT = 200
const CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT = 4800
const DIGITAL_MESSAGES_PER_STANDARD_SESSION = 10

// ─── Five9 pricing model ──────────────────────────────────────────────
const FIVE9_PRICE_PER_SESSION = 247

// ─── Five9 new consumption-based pricing model ─────────────────────────
const FIVE9_NEW_VOICE_PRICE_PER_MIN = 0.25

// ─── Five9 Digital pricing model ───────────────────────────────────────
const FIVE9_DIGITAL_LIST_PRICE = 115
const FIVE9_DIGITAL_CREDITS_PER_UNIT = 20000

// ─── Google pricing model ─────────────────────────────────────────────
const GOOGLE_FLOWS_PRICE_PER_SECOND = 0.001
const GOOGLE_PLAYBOOKS_PRICE_PER_SECOND = 0.002
const GOOGLE_DIGITAL_PRICE_PER_SESSION = 0.02 // third-party sourced (Google's own pricing page could not be scraped) — flat per text session, any turn count

// ─── Amazon Connect / Lex pricing ─────────────────────────────────────
const AMAZON_LEX_SPEECH_PRICE = 0.004
const AMAZON_CONNECT_BASIC_PRICE_PER_MIN = 0.018
const AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN = 0.038
const AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE = 0.0015

// ─── Genesys pricing model — all 4 AI products share one org-wide monthly
// token pool; each solution card below is priced independently against that
// pool rather than modeling true cross-product pooling (see billing notes) ──
const GENESYS_PRICE_PER_TOKEN = 1
const GENESYS_FREE_TOKENS_NAMED = 250
const GENESYS_FREE_TOKENS_CONCURRENT = 350
const GENESYS_VOICE_BOT_MINUTES_PER_TOKEN = 17
const GENESYS_DIGITAL_BOT_SESSIONS_PER_TOKEN = 51
const GENESYS_VIRTUAL_AGENT_TOKENS_PER_INTERACTION = 0.5
const GENESYS_AGENTIC_VIRTUAL_AGENT_TOKENS_PER_INTERACTION = 1.2

// ─── Zoom Contact Center pricing model ─────────────────────────────────
const ZOOM_VOICE_PRICE_PER_MIN = 0.29
const ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT = 0.048

// ─── Cisco AI Assistant pricing model — one unit pools voice minutes AND
// digital sessions proportionally (not two separate pools). AI Assistant + AI QM
// has two real capacity tiers per unit, same $/unit price either way: "single
// workload" (an interaction only uses AI Assistant OR AI QM features) gets the
// larger 2,500 min / 1,600 session capacity; "dual workload" (an interaction
// uses both) gets the smaller 1,500 min / 1,000 session capacity. Modeled as
// two separate solutions below rather than one input asking for a usage mix ──
const CISCO_ASSISTANT_UNIT_PRICE = 34.50
const CISCO_ASSISTANT_MINUTES_PER_UNIT = 1500
const CISCO_ASSISTANT_SESSIONS_PER_UNIT = 1000
const CISCO_ASSISTANT_QM_UNIT_PRICE = 57.50
const CISCO_ASSISTANT_QM_SINGLE_MINUTES_PER_UNIT = 2500
const CISCO_ASSISTANT_QM_SINGLE_SESSIONS_PER_UNIT = 1600
const CISCO_ASSISTANT_QM_DUAL_MINUTES_PER_UNIT = 1500
const CISCO_ASSISTANT_QM_DUAL_SESSIONS_PER_UNIT = 1000

// ─── Genesys AI Assistant (Copilot + Analytics) pricing — shares the same
// $1/token pool as the Genesys AI Agent products. Copilot and Speech & Text
// Analytics meter tokens per agent seat (Named/Concurrent); the rest meter per
// interaction, same shape as the AI Agent digital-bot products ─────────────
const GENESYS_COPILOT_TOKENS_PER_SEAT_NAMED = 40
const GENESYS_COPILOT_TOKENS_PER_SEAT_CONCURRENT = 60
const GENESYS_AI_SCORING_EVALUATIONS_PER_TOKEN = 20
const GENESYS_AI_TRANSLATE_INTERACTIONS_PER_TOKEN = 2
const GENESYS_AI_SUMMARY_ITEMS_PER_TOKEN = 50
const GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_NAMED = 30
const GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_CONCURRENT = 45
const GENESYS_SOCIAL_POSTS_PER_TOKEN = 400

const SOLUTIONS = {
  new_scripted: {
    id: 'new_scripted',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted',
    billingMode: 'minute',
    minutesPerUnit: NEW_SCRIPTED_MINUTES_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit · metered to the second`,
    accentColor: '#00bceb',
    isNew: true,
    pricingGroup: 'cisco_new',
  },
  new_autonomous: {
    id: 'new_autonomous',
    name: 'Cisco AI Agent',
    subtitle: 'Autonomous',
    billingMode: 'minute',
    minutesPerUnit: NEW_AUTONOMOUS_MINUTES_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit · metered to the second`,
    accentColor: '#6abf4b',
    isNew: true,
    pricingGroup: 'cisco_new',
  },
  new_split: {
    id: 'new_split',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted + Autonomous (Split)',
    billingMode: 'ciscoSplit',
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `Blended minute split · ${NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit Scripted · ${NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit Autonomous`,
    accentColor: '#7C4DFF',
    isNew: false,
  },
  cisco_digital_scripted: {
    id: 'cisco_digital_scripted',
    name: 'Cisco AI Agent',
    subtitle: 'Scripted Digital',
    billingMode: 'digital',
    sessionsPerUnit: CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · per 10-message session standard`,
    accentColor: '#00bceb',
    isNew: true,
    pricingGroup: 'cisco_new',
  },
  cisco_digital_autonomous: {
    id: 'cisco_digital_autonomous',
    name: 'Cisco AI Agent',
    subtitle: 'Autonomous Digital',
    billingMode: 'digital',
    sessionsPerUnit: CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT,
    listPrice: NEW_UNIT_LIST_PRICE,
    description: `${CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · per 10-message session standard`,
    accentColor: '#6abf4b',
    isNew: true,
    pricingGroup: 'cisco_new',
  },
  five9_iva: {
    id: 'five9_iva',
    name: 'Five9',
    subtitle: 'Voice IVA (Old — Per Session)',
    billingMode: 'concurrent',
    unitPrice: FIVE9_PRICE_PER_SESSION,
    description: `$${FIVE9_PRICE_PER_SESSION}/concurrent session · peak calls × price`,
    accentColor: '#e05d44',
    isNew: false,
  },
  five9_voice_new: {
    id: 'five9_voice_new',
    name: 'Five9',
    subtitle: 'Voice IVA (New — Per Minute)',
    billingMode: 'roundedTotalMinute',
    unitPrice: FIVE9_NEW_VOICE_PRICE_PER_MIN,
    description: `$${FIVE9_NEW_VOICE_PRICE_PER_MIN}/min · total usage rounded up to the minute · consumption-based`,
    accentColor: '#e05d44',
    isNew: false,
  },
  five9_digital: {
    id: 'five9_digital',
    name: 'Five9',
    subtitle: 'Digital IVA',
    billingMode: 'messageCredits',
    unitPrice: FIVE9_DIGITAL_LIST_PRICE,
    creditsPerUnit: FIVE9_DIGITAL_CREDITS_PER_UNIT,
    description: `${FIVE9_DIGITAL_CREDITS_PER_UNIT.toLocaleString()} credits/unit · 1 credit per message sent or received`,
    accentColor: '#e05d44',
    isNew: false,
  },
  google_flows: {
    id: 'google_flows',
    name: 'Google',
    subtitle: 'Conversational Agents (Flows)',
    billingMode: 'second',
    unitPrice: GOOGLE_FLOWS_PRICE_PER_SECOND,
    description: `$${GOOGLE_FLOWS_PRICE_PER_SECOND}/sec · total seconds × price`,
    accentColor: '#4285f4',
    isNew: false,
  },
  google_playbooks: {
    id: 'google_playbooks',
    name: 'Google',
    subtitle: 'Conversational Agents (Playbooks)',
    billingMode: 'second',
    unitPrice: GOOGLE_PLAYBOOKS_PRICE_PER_SECOND,
    description: `$${GOOGLE_PLAYBOOKS_PRICE_PER_SECOND}/sec · total seconds × price`,
    accentColor: '#ea4335',
    isNew: false,
  },
  google_digital: {
    id: 'google_digital',
    name: 'Google',
    subtitle: 'Conversational Agents (Digital)',
    billingMode: 'engagement',
    unitPrice: GOOGLE_DIGITAL_PRICE_PER_SESSION,
    description: `$${GOOGLE_DIGITAL_PRICE_PER_SESSION}/text session · flat, regardless of turn count`,
    accentColor: '#34a853',
    isNew: false,
  },
  amazon_connect_basic: {
    id: 'amazon_connect_basic',
    name: 'Amazon Connect',
    subtitle: 'Basic — Lex Voice',
    billingMode: 'lex',
    unitPrice: AMAZON_LEX_SPEECH_PRICE,
    connectPricePerMin: AMAZON_CONNECT_BASIC_PRICE_PER_MIN,
    description: `$${AMAZON_LEX_SPEECH_PRICE}/speech req · +$${AMAZON_CONNECT_BASIC_PRICE_PER_MIN}/min Connect`,
    accentColor: '#FF9900',
    isNew: false,
    pricingGroup: 'amazon_connect_lex',
    connectOverrideKey: 'amazon_connect_lex_min',
  },
  amazon_connect_ai_bundled: {
    id: 'amazon_connect_ai_bundled',
    name: 'Amazon Connect',
    subtitle: 'AI Bundled — Lex Voice',
    billingMode: 'lex',
    unitPrice: AMAZON_LEX_SPEECH_PRICE,
    connectPricePerMin: AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN,
    description: `$${AMAZON_LEX_SPEECH_PRICE}/speech req · +$${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN}/min Connect (AI bundled, no extra AI charges)`,
    accentColor: '#FF9900',
    isNew: false,
    pricingGroup: 'amazon_connect_lex',
    connectOverrideKey: 'amazon_connect_lex_min',
  },
  amazon_connect_basic_digital: {
    id: 'amazon_connect_basic_digital',
    name: 'Amazon Connect',
    subtitle: 'Basic — Digital',
    billingMode: 'perMessage',
    unitPrice: AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE,
    description: `$${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message · no bundling`,
    accentColor: '#FF9900',
    isNew: false,
    pricingGroup: 'amazon_connect_digital',
  },
  amazon_connect_ai_bundled_digital: {
    id: 'amazon_connect_ai_bundled_digital',
    name: 'Amazon Connect',
    subtitle: 'AI Bundled — Digital',
    billingMode: 'perMessage',
    unitPrice: AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE,
    description: `$${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message · no bundling`,
    accentColor: '#FF9900',
    isNew: false,
    pricingGroup: 'amazon_connect_digital',
  },
  genesys_voice_bots: {
    id: 'genesys_voice_bots',
    name: 'Genesys',
    subtitle: 'Voice Bots',
    billingMode: 'genesysVoiceToken',
    minutesPerToken: GENESYS_VOICE_BOT_MINUTES_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_VOICE_BOT_MINUTES_PER_TOKEN} min/token · call length rounded up to the nearest 15 sec · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_digital_bots: {
    id: 'genesys_digital_bots',
    name: 'Genesys',
    subtitle: 'Digital Bots',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_DIGITAL_BOT_SESSIONS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_DIGITAL_BOT_SESSIONS_PER_TOKEN} sessions/token · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_virtual_agent: {
    id: 'genesys_virtual_agent',
    name: 'Genesys',
    subtitle: 'Virtual Agent',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: GENESYS_VIRTUAL_AGENT_TOKENS_PER_INTERACTION,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_VIRTUAL_AGENT_TOKENS_PER_INTERACTION} tokens/interaction · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_agentic_virtual_agent: {
    id: 'genesys_agentic_virtual_agent',
    name: 'Genesys',
    subtitle: 'Agentic Virtual Agent',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: GENESYS_AGENTIC_VIRTUAL_AGENT_TOKENS_PER_INTERACTION,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_AGENTIC_VIRTUAL_AGENT_TOKENS_PER_INTERACTION} tokens/interaction · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  zoom_voice: {
    id: 'zoom_voice',
    name: 'Zoom Contact Center',
    subtitle: 'Virtual Agent — Voice',
    billingMode: 'roundedMinute',
    unitPrice: ZOOM_VOICE_PRICE_PER_MIN,
    description: `$${ZOOM_VOICE_PRICE_PER_MIN}/min · rounded up to the minute`,
    accentColor: '#0B5CFF',
    isNew: false,
  },
  zoom_digital: {
    id: 'zoom_digital',
    name: 'Zoom Contact Center',
    subtitle: 'Virtual Agent — Digital',
    billingMode: 'engagement',
    unitPrice: ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT,
    description: `$${ZOOM_DIGITAL_PRICE_PER_ENGAGEMENT}/engagement`,
    accentColor: '#0B5CFF',
    isNew: false,
  },
  cisco_ai_assistant: {
    id: 'cisco_ai_assistant',
    name: 'Cisco AI Assistant',
    subtitle: '',
    billingMode: 'ciscoAssistantBlend',
    minutesPerUnit: CISCO_ASSISTANT_MINUTES_PER_UNIT,
    sessionsPerUnit: CISCO_ASSISTANT_SESSIONS_PER_UNIT,
    listPrice: CISCO_ASSISTANT_UNIT_PRICE,
    description: `$${CISCO_ASSISTANT_UNIT_PRICE}/unit (overage) · ${CISCO_ASSISTANT_MINUTES_PER_UNIT.toLocaleString()} voice min OR ${CISCO_ASSISTANT_SESSIONS_PER_UNIT.toLocaleString()} digital sessions per unit, proportional blend`,
    accentColor: '#00bceb',
    isNew: true,
    pricingGroup: 'cisco_assistant',
  },
  cisco_ai_qm: {
    id: 'cisco_ai_qm',
    name: 'Cisco AI Quality Management',
    subtitle: '',
    billingMode: 'ciscoAssistantBlend',
    minutesPerUnit: CISCO_ASSISTANT_MINUTES_PER_UNIT,
    sessionsPerUnit: CISCO_ASSISTANT_SESSIONS_PER_UNIT,
    listPrice: CISCO_ASSISTANT_UNIT_PRICE,
    description: `$${CISCO_ASSISTANT_UNIT_PRICE}/unit (overage) · same unit pricing as AI Assistant · ${CISCO_ASSISTANT_MINUTES_PER_UNIT.toLocaleString()} voice min OR ${CISCO_ASSISTANT_SESSIONS_PER_UNIT.toLocaleString()} digital sessions per unit, proportional blend`,
    accentColor: '#33c9ea',
    isNew: true,
    pricingGroup: 'cisco_assistant',
  },
  cisco_ai_assistant_qm_single: {
    id: 'cisco_ai_assistant_qm_single',
    name: 'Cisco AI Assistant + AI QM',
    subtitle: 'Single Workload',
    billingMode: 'ciscoAssistantBlend',
    minutesPerUnit: CISCO_ASSISTANT_QM_SINGLE_MINUTES_PER_UNIT,
    sessionsPerUnit: CISCO_ASSISTANT_QM_SINGLE_SESSIONS_PER_UNIT,
    listPrice: CISCO_ASSISTANT_QM_UNIT_PRICE,
    description: `$${CISCO_ASSISTANT_QM_UNIT_PRICE}/unit (overage) · interactions use only AI Assistant OR only AI QM features · ${CISCO_ASSISTANT_QM_SINGLE_MINUTES_PER_UNIT.toLocaleString()} voice min OR ${CISCO_ASSISTANT_QM_SINGLE_SESSIONS_PER_UNIT.toLocaleString()} digital sessions per unit`,
    accentColor: '#0098da',
    isNew: true,
    pricingGroup: 'cisco_assistant_qm',
  },
  cisco_ai_assistant_qm_dual: {
    id: 'cisco_ai_assistant_qm_dual',
    name: 'Cisco AI Assistant + AI QM',
    subtitle: 'Dual Workload',
    billingMode: 'ciscoAssistantBlend',
    minutesPerUnit: CISCO_ASSISTANT_QM_DUAL_MINUTES_PER_UNIT,
    sessionsPerUnit: CISCO_ASSISTANT_QM_DUAL_SESSIONS_PER_UNIT,
    listPrice: CISCO_ASSISTANT_QM_UNIT_PRICE,
    description: `$${CISCO_ASSISTANT_QM_UNIT_PRICE}/unit (overage) · interactions use both AI Assistant AND AI QM features · ${CISCO_ASSISTANT_QM_DUAL_MINUTES_PER_UNIT.toLocaleString()} voice min OR ${CISCO_ASSISTANT_QM_DUAL_SESSIONS_PER_UNIT.toLocaleString()} digital sessions per unit`,
    accentColor: '#0071a6',
    isNew: true,
    pricingGroup: 'cisco_assistant_qm',
  },
  genesys_copilot: {
    id: 'genesys_copilot',
    name: 'Genesys',
    subtitle: 'Predictive Agent Copilot',
    billingMode: 'genesysSeatToken',
    tokensPerSeatNamed: GENESYS_COPILOT_TOKENS_PER_SEAT_NAMED,
    tokensPerSeatConcurrent: GENESYS_COPILOT_TOKENS_PER_SEAT_CONCURRENT,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_COPILOT_TOKENS_PER_SEAT_NAMED} tokens/seat (Named) · ${GENESYS_COPILOT_TOKENS_PER_SEAT_CONCURRENT} tokens/seat (Concurrent) · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_ai_scoring: {
    id: 'genesys_ai_scoring',
    name: 'Genesys',
    subtitle: 'AI Scoring',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_AI_SCORING_EVALUATIONS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_AI_SCORING_EVALUATIONS_PER_TOKEN} evaluations/token · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_ai_translate: {
    id: 'genesys_ai_translate',
    name: 'Genesys',
    subtitle: 'AI Translate',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_AI_TRANSLATE_INTERACTIONS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_AI_TRANSLATE_INTERACTIONS_PER_TOKEN} interactions translated/token · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_ai_summary: {
    id: 'genesys_ai_summary',
    name: 'Genesys',
    subtitle: 'AI Summary & Insights',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_AI_SUMMARY_ITEMS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_AI_SUMMARY_ITEMS_PER_TOKEN} summaries & insights/token · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_speech_text_analytics: {
    id: 'genesys_speech_text_analytics',
    name: 'Genesys',
    subtitle: 'Speech & Text Analytics',
    billingMode: 'genesysSeatToken',
    tokensPerSeatNamed: GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_NAMED,
    tokensPerSeatConcurrent: GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_CONCURRENT,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_NAMED} tokens/seat (Named) · ${GENESYS_SPEECH_TEXT_ANALYTICS_TOKENS_PER_SEAT_CONCURRENT} tokens/seat (Concurrent) · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_social_ingestion: {
    id: 'genesys_social_ingestion',
    name: 'Genesys',
    subtitle: 'Social — Ingestion',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_SOCIAL_POSTS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_SOCIAL_POSTS_PER_TOKEN.toLocaleString()} public social posts ingested/token, per channel · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
  genesys_social_response: {
    id: 'genesys_social_response',
    name: 'Genesys',
    subtitle: 'Social — Post Responses',
    billingMode: 'genesysInteractionToken',
    tokensPerInteraction: 1 / GENESYS_SOCIAL_POSTS_PER_TOKEN,
    unitPrice: GENESYS_PRICE_PER_TOKEN,
    description: `$${GENESYS_PRICE_PER_TOKEN}/token · ${GENESYS_SOCIAL_POSTS_PER_TOKEN.toLocaleString()} outbound public social post responses/token, per channel · ${GENESYS_FREE_TOKENS_NAMED}/${GENESYS_FREE_TOKENS_CONCURRENT} free tokens (Named/Concurrent)`,
    accentColor: '#8B2FC9',
    isNew: false,
    pricingGroup: 'genesys',
    poolGroup: 'genesys',
  },
}

const AGENT_SOLUTION_ORDER = [
  'new_scripted', 'new_autonomous', 'new_split',
  'cisco_digital_scripted', 'cisco_digital_autonomous',
  'five9_iva', 'five9_voice_new', 'five9_digital',
  'google_flows', 'google_playbooks', 'google_digital',
  'amazon_connect_basic', 'amazon_connect_ai_bundled',
  'amazon_connect_basic_digital', 'amazon_connect_ai_bundled_digital',
  'genesys_voice_bots', 'genesys_digital_bots', 'genesys_virtual_agent', 'genesys_agentic_virtual_agent',
  'zoom_voice', 'zoom_digital',
]

const ASSISTANT_SOLUTION_ORDER = [
  'cisco_ai_assistant', 'cisco_ai_qm', 'cisco_ai_assistant_qm_single', 'cisco_ai_assistant_qm_dual',
  'genesys_copilot', 'genesys_ai_scoring', 'genesys_ai_translate', 'genesys_ai_summary',
  'genesys_speech_text_analytics', 'genesys_social_ingestion', 'genesys_social_response',
]

function calculateResults({ numCalls, avgCallLength, peakConcurrent, numSpeechRequests, solution, overridePrice, overrideConnectPrice, numDigitalInteractions = 0, avgMessagesPerSession = 10, splitTotalSeconds = 0, splitScriptedPct = 0, blendVoiceMinutes = 0, blendDigitalSessions = 0, numSeats = 0, licenseType }) {
  if (solution.billingMode === 'ciscoAssistantBlend') {
    const listPrice = solution.listPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const voiceUnits = blendVoiceMinutes / solution.minutesPerUnit
    const digitalUnits = blendDigitalSessions / solution.sessionsPerUnit
    const unitsRequired = Math.ceil(voiceUnits + digitalUnits)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'ciscoAssistantBlend', blendVoiceMinutes, blendDigitalSessions, voiceUnits, digitalUnits, unitsRequired, totalCost, effectiveUnitPrice, listPrice }
  }

  if (solution.billingMode === 'genesysSeatToken') {
    // Raw/gross — see the note in the genesysInteractionToken branch below; the free
    // token allowance is applied once across the whole Genesys pool, not per line.
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const tokensPerSeat = licenseType === 'concurrent' ? solution.tokensPerSeatConcurrent : solution.tokensPerSeatNamed
    const rawTokens = numSeats * tokensPerSeat
    const totalCost = rawTokens * effectiveUnitPrice
    return { billingMode: 'genesysSeatToken', numSeats, tokensPerSeat, rawTokens, totalCost, effectiveUnitPrice, listPrice }
  }

  if (solution.billingMode === 'lex') {
    const effectiveSpeechPrice = overridePrice > 0 ? overridePrice : AMAZON_LEX_SPEECH_PRICE
    const effectiveConnectPrice = overrideConnectPrice > 0 ? overrideConnectPrice : solution.connectPricePerMin
    const lexSpeechCost = numSpeechRequests * numCalls * effectiveSpeechPrice
    const connectTotalCost = numCalls * (avgCallLength / 60) * effectiveConnectPrice
    return {
      billingMode: 'lex',
      numSpeechRequests,
      effectiveSpeechPrice,
      effectiveConnectPrice,
      lexSpeechCost,
      connectTotalCost,
      totalCostWithoutConnect: lexSpeechCost,
      totalCostWithConnect: lexSpeechCost + connectTotalCost,
      numCalls,
      avgCallLengthSecs: avgCallLength,
    }
  }

  if (solution.billingMode === 'digital') {
    const normalizedSessions = numDigitalInteractions * (avgMessagesPerSession / DIGITAL_MESSAGES_PER_STANDARD_SESSION)
    const listPrice = solution.listPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const unitsRequired = Math.ceil(normalizedSessions / solution.sessionsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'digital', normalizedSessions, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'engagement') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalCost = numDigitalInteractions * effectiveUnitPrice
    return { billingMode: 'engagement', totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions }
  }

  if (solution.billingMode === 'messageCredits') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalMessages = numDigitalInteractions * avgMessagesPerSession
    const unitsRequired = Math.ceil(totalMessages / solution.creditsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'messageCredits', totalMessages, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'perMessage') {
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const totalMessages = numDigitalInteractions * avgMessagesPerSession
    const totalCost = totalMessages * effectiveUnitPrice
    return { billingMode: 'perMessage', totalMessages, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions, avgMsgs: avgMessagesPerSession }
  }

  if (solution.billingMode === 'genesysInteractionToken') {
    // Raw/gross — no free-tier deduction here. All Genesys lines share one org-wide
    // token pool, so the free allowance is applied once, across every Genesys line
    // in the cart, by computeGenesysPool() — not independently per line.
    const listPrice = solution.unitPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const rawTokens = numDigitalInteractions * solution.tokensPerInteraction
    const totalCost = rawTokens * effectiveUnitPrice
    return { billingMode: 'genesysInteractionToken', rawTokens, totalCost, effectiveUnitPrice, listPrice, numInteractions: numDigitalInteractions }
  }

  if (solution.billingMode === 'ciscoSplit') {
    const pct = Math.min(100, Math.max(0, splitScriptedPct))
    const listPrice = solution.listPrice
    const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice
    const splitTotalMinutes = splitTotalSeconds / 60
    const scriptedMinutes = splitTotalMinutes * (pct / 100)
    const autonomousMinutes = splitTotalMinutes * (1 - pct / 100)
    const scriptedUnits = Math.ceil(scriptedMinutes / NEW_SCRIPTED_MINUTES_PER_UNIT)
    const autonomousUnits = Math.ceil(autonomousMinutes / NEW_AUTONOMOUS_MINUTES_PER_UNIT)
    const unitsRequired = scriptedUnits + autonomousUnits
    const scriptedCost = scriptedUnits * effectiveUnitPrice
    const autonomousCost = autonomousUnits * effectiveUnitPrice
    return {
      billingMode: 'ciscoSplit', splitTotalSeconds, splitTotalMinutes, splitScriptedPct: pct,
      scriptedMinutes, autonomousMinutes, scriptedUnits, autonomousUnits,
      scriptedCost, autonomousCost, unitsRequired,
      totalCost: scriptedCost + autonomousCost,
      effectiveUnitPrice, listPrice,
    }
  }

  const listPrice = solution.isNew ? solution.listPrice : solution.unitPrice
  const effectiveUnitPrice = overridePrice > 0 ? overridePrice : listPrice

  if (solution.billingMode === 'session') {
    const sessionsPerCall = Math.ceil(avgCallLength / SESSION_BLOCK_SECONDS)
    const totalSessions = numCalls * sessionsPerCall
    const unitsRequired = Math.ceil(totalSessions / solution.sessionsPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'session', sessionsPerCall, totalSessions, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'minute') {
    const totalMinutes = numCalls * (avgCallLength / 60)
    const unitsRequired = Math.ceil(totalMinutes / solution.minutesPerUnit)
    const totalCost = unitsRequired * effectiveUnitPrice
    return { billingMode: 'minute', totalMinutes, avgCallLengthSecs: avgCallLength, unitsRequired, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'roundedMinute') {
    const minutesPerCall = Math.ceil(avgCallLength / 60)
    const totalMinutes = numCalls * minutesPerCall
    const totalCost = totalMinutes * effectiveUnitPrice
    return { billingMode: 'roundedMinute', minutesPerCall, totalMinutes, avgCallLengthSecs: avgCallLength, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'roundedTotalMinute') {
    const totalSeconds = numCalls * avgCallLength
    const totalMinutes = Math.ceil(totalSeconds / 60)
    const totalCost = totalMinutes * effectiveUnitPrice
    return { billingMode: 'roundedTotalMinute', totalSeconds, totalMinutes, avgCallLengthSecs: avgCallLength, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else if (solution.billingMode === 'concurrent') {
    const totalCost = peakConcurrent * effectiveUnitPrice
    return { billingMode: 'concurrent', peakConcurrent, totalCost, effectiveUnitPrice, listPrice }
  } else if (solution.billingMode === 'genesysVoiceToken') {
    // Raw/gross — see the note in the genesysInteractionToken branch above; the free
    // token allowance is applied once across the whole Genesys pool, not per line.
    const roundedSecondsPerCall = Math.ceil(avgCallLength / 15) * 15
    const totalMinutes = (numCalls * roundedSecondsPerCall) / 60
    const rawTokens = totalMinutes / solution.minutesPerToken
    const totalCost = rawTokens * effectiveUnitPrice
    return { billingMode: 'genesysVoiceToken', roundedSecondsPerCall, totalMinutes, rawTokens, totalCost, effectiveUnitPrice, listPrice, numCalls }
  } else {
    const totalSeconds = numCalls * avgCallLength
    const totalCost = totalSeconds * effectiveUnitPrice
    return { billingMode: 'second', totalSeconds, avgCallLengthSecs: avgCallLength, totalCost, effectiveUnitPrice, listPrice, numCalls }
  }
}


// ─── Sub-components ───────────────────────────────────────────────────

function InputField({ label, hint, value, onChange, min = 0, max, step = 1, prefix, suffix, placeholder }) {
  return (
    <div className="input-field">
      <label>{label}</label>
      {hint && <span className="input-hint">{hint}</span>}
      <div className="input-wrapper">
        {prefix && <span className="input-adornment prefix">{prefix}</span>}
        <input
          type="number"
          value={value}
          min={min}
          {...(max !== undefined ? { max } : {})}
          step={step}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        {suffix && <span className="input-adornment suffix">{suffix}</span>}
      </div>
    </div>
  )
}

function BreakdownRow({ label, value }) {
  return (
    <div className="breakdown-row">
      <span className="breakdown-label">{label}</span>
      <span className="breakdown-value">{value}</span>
    </div>
  )
}

function ResultCard({ solution, results, isReady, lineLabel }) {
  const { billingMode } = results
  const unitsRequired = results.unitsRequired
  const totalCost = results.totalCost
  const effectiveUnitPrice = results.effectiveUnitPrice
  const listPrice = results.listPrice
  const usingCustomPrice = effectiveUnitPrice !== listPrice

  return (
    <div className="result-card" style={{ '--accent': solution.accentColor }}>
      <div className="result-card-header" style={{ borderTopColor: solution.accentColor }}>
        <div className="card-header-top">
          <div className="solution-name">{solution.name}</div>
          {lineLabel && <div className="line-label-badge">{lineLabel}</div>}
        </div>
        {solution.subtitle && (
          <div className="solution-subtitle" style={{ color: solution.accentColor }}>
            {solution.subtitle}
          </div>
        )}
        <div className="solution-desc">{solution.description}</div>
      </div>

      <div className="result-card-body">
        {isReady ? (
          <>
            <div className="breakdown">
              {billingMode === 'session' && (
                <>
                  <BreakdownRow label="Sessions per call" value={results.sessionsPerCall.toLocaleString()} />
                  <BreakdownRow label="Total sessions" value={results.totalSessions.toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={<span>${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>}
                  />
                </>
              )}
              {billingMode === 'minute' && (
                <>
                  <BreakdownRow label="Avg call length" value={`${results.avgCallLengthSecs} sec`} />
                  <BreakdownRow label="Total minutes" value={results.totalMinutes.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow label="AI Agent Units required" value={unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && solution.isNew && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'roundedMinute' && (
                <>
                  <BreakdownRow label="Minutes per call (rounded up)" value={results.minutesPerCall.toLocaleString()} />
                  <BreakdownRow label="Total minutes" value={results.totalMinutes.toLocaleString()} />
                  <BreakdownRow
                    label="Price per minute"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'roundedTotalMinute' && (
                <>
                  <BreakdownRow label="Total seconds" value={results.totalSeconds.toLocaleString()} />
                  <BreakdownRow label="Total minutes (rounded up)" value={results.totalMinutes.toLocaleString()} />
                  <BreakdownRow
                    label="Price per minute"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'concurrent' && (
                <>
                  <BreakdownRow label="Peak concurrent sessions" value={results.peakConcurrent.toLocaleString()} />
                  <BreakdownRow
                    label="Price per concurrent session"
                    value={`$${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'second' && (
                <>
                  <BreakdownRow label="Avg call length" value={`${results.avgCallLengthSecs} sec`} />
                  <BreakdownRow label="Total seconds" value={results.totalSeconds.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow
                    label="Price per second"
                    value={
                      <span>
                        ${effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        {usingCustomPrice && <span className="price-tag customer"> customer</span>}
                        {!usingCustomPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'genesysVoiceToken' && (
                <>
                  <BreakdownRow label="Total minutes (15-sec rounded)" value={results.totalMinutes.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow label="Tokens (gross, pre-pool discount)" value={results.rawTokens.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow
                    label="Price per token"
                    value={`$${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'genesysInteractionToken' && (
                <>
                  <BreakdownRow label="Interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Tokens (gross, pre-pool discount)" value={results.rawTokens.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow
                    label="Price per token"
                    value={`$${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'genesysSeatToken' && (
                <>
                  <BreakdownRow label="Agent seats" value={results.numSeats.toLocaleString()} />
                  <BreakdownRow label="Tokens per seat" value={results.tokensPerSeat.toLocaleString()} />
                  <BreakdownRow label="Tokens (gross, pre-pool discount)" value={results.rawTokens.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow
                    label="Price per token"
                    value={`$${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
              {billingMode === 'ciscoAssistantBlend' && (
                <>
                  <BreakdownRow label="Voice minutes" value={results.blendVoiceMinutes.toLocaleString()} />
                  <BreakdownRow label="Digital sessions" value={results.blendDigitalSessions.toLocaleString()} />
                  <BreakdownRow label="Blended units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'digital' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/session" value={results.avgMsgs} />
                  <BreakdownRow label="Normalized sessions" value={Math.ceil(results.normalizedSessions).toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'engagement' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow
                    label="Price per engagement"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'messageCredits' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/interaction" value={results.avgMsgs} />
                  <BreakdownRow label="Total messages (credits)" value={Math.ceil(results.totalMessages).toLocaleString()} />
                  <BreakdownRow label="AI Agent Units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'perMessage' && (
                <>
                  <BreakdownRow label="Digital interactions" value={results.numInteractions.toLocaleString()} />
                  <BreakdownRow label="Avg messages/interaction" value={results.avgMsgs} />
                  <BreakdownRow label="Total messages" value={Math.ceil(results.totalMessages).toLocaleString()} />
                  <BreakdownRow
                    label="Price per message"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'ciscoSplit' && (
                <>
                  <BreakdownRow label="Total seconds" value={results.splitTotalSeconds.toLocaleString()} />
                  <BreakdownRow label="Total minutes" value={results.splitTotalMinutes.toLocaleString('en-US', { maximumFractionDigits: 2 })} />
                  <BreakdownRow label="Split" value={`${results.splitScriptedPct}% Scripted / ${100 - results.splitScriptedPct}% Autonomous`} />
                  <BreakdownRow label="Scripted minutes" value={results.scriptedMinutes.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow label="Scripted units" value={results.scriptedUnits.toLocaleString()} />
                  <BreakdownRow label="Autonomous minutes" value={results.autonomousMinutes.toLocaleString('en-US', { maximumFractionDigits: 0 })} />
                  <BreakdownRow label="Autonomous units" value={results.autonomousUnits.toLocaleString()} />
                  <BreakdownRow label="Total units required" value={results.unitsRequired.toLocaleString()} />
                  <BreakdownRow
                    label="Price per unit"
                    value={
                      <span>
                        ${results.effectiveUnitPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {results.effectiveUnitPrice !== results.listPrice && <span className="price-tag customer"> customer</span>}
                        {results.effectiveUnitPrice === results.listPrice && <span className="price-tag list"> list</span>}
                      </span>
                    }
                  />
                </>
              )}
              {billingMode === 'lex' && (
                <>
                  {results.numSpeechRequests !== null && (
                    <BreakdownRow label="Speech requests per call" value={results.numSpeechRequests.toLocaleString()} />
                  )}
                  <BreakdownRow label="Total speech requests" value={results.lexSpeechCost > 0 ? (results.lexSpeechCost / results.effectiveSpeechPrice).toLocaleString('en-US', { maximumFractionDigits: 0 }) : '0'} />
                  <BreakdownRow
                    label="Lex per speech request"
                    value={`$${results.effectiveSpeechPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`}
                  />
                  <BreakdownRow
                    label="Lex speech cost"
                    value={`$${results.lexSpeechCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                  <BreakdownRow
                    label="Connect rate (per min)"
                    value={`$${results.effectiveConnectPrice.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`}
                  />
                  <BreakdownRow
                    label="Connect total"
                    value={`$${results.connectTotalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  />
                </>
              )}
            </div>

            {billingMode === 'lex' ? (
              <div className="lex-cost-blocks">
                <div className="cost-block">
                  <div className="cost-label">Lex Only (without Connect)</div>
                  <div className="cost-value">
                    ${results.totalCostWithoutConnect.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">
                    ${(results.totalCostWithoutConnect / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                </div>
                <div className="cost-block cost-block-connect">
                  <div className="cost-label">Lex + Connect Charges</div>
                  <div className="cost-value">
                    ${results.totalCostWithConnect.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">
                    ${(results.totalCostWithConnect / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                </div>
              </div>
            ) : billingMode === 'ciscoSplit' ? (
              <div className="lex-cost-blocks">
                <div className="cost-block">
                  <div className="cost-label">Scripted Tier</div>
                  <div className="cost-value">
                    ${results.scriptedCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.scriptedUnits.toLocaleString()} units</div>
                </div>
                <div className="cost-block">
                  <div className="cost-label">Autonomous Tier</div>
                  <div className="cost-value">
                    ${results.autonomousCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.autonomousUnits.toLocaleString()} units</div>
                </div>
                <div className="cost-block cost-block-connect">
                  <div className="cost-label">Blended Total</div>
                  <div className="cost-value">
                    ${results.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className="cost-per-call">{results.unitsRequired.toLocaleString()} units</div>
                </div>
              </div>
            ) : (
              <div className="cost-block">
                <div className="cost-label">
                  {['genesysVoiceToken', 'genesysInteractionToken', 'genesysSeatToken'].includes(billingMode) ? 'Gross Cost (before pool discount)' : 'Estimated List Price'}
                </div>
                <div className="cost-value">
                  ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                {['genesysVoiceToken', 'genesysInteractionToken', 'genesysSeatToken'].includes(billingMode) && (
                  <div className="cost-per-call">See Cart Summary below for this line's share of the shared Genesys token discount</div>
                )}
                {['digital', 'engagement', 'messageCredits', 'perMessage', 'genesysInteractionToken'].includes(billingMode) && results.numInteractions > 0 && (
                  <div className="cost-per-call">
                    ${(totalCost / results.numInteractions).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per interaction
                  </div>
                )}
                {!['concurrent', 'digital', 'engagement', 'messageCredits', 'perMessage', 'genesysInteractionToken'].includes(billingMode) && results.numCalls > 0 && (
                  <div className="cost-per-call">
                    ${(totalCost / results.numCalls).toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 })} per call
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="placeholder-msg">
            {solution.billingMode === 'concurrent'
              ? 'Enter peak concurrent calls to see results'
              : solution.billingMode === 'lex'
              ? 'Enter call volume and speech requests to see results'
              : ['digital', 'engagement', 'messageCredits', 'perMessage', 'genesysInteractionToken'].includes(solution.billingMode)
              ? 'Enter digital interaction volume to see results'
              : solution.billingMode === 'ciscoSplit'
              ? 'Enter total seconds and scripted split % to see results'
              : solution.billingMode === 'genesysSeatToken'
              ? 'Enter agent seat count to see results'
              : solution.billingMode === 'ciscoAssistantBlend'
              ? 'Enter voice minutes and/or digital sessions to see results'
              : 'Enter call volume details to see results'}
          </div>
        )}
      </div>
    </div>
  )
}

function BillingNote({ comparisonIds }) {
  const solutions = comparisonIds.map((id) => SOLUTIONS[id])
  const hasSession = solutions.some((s) => s.billingMode === 'session')
  const hasMinute = solutions.some((s) => s.billingMode === 'minute')
  const hasConcurrent = solutions.some((s) => s.billingMode === 'concurrent')
  const hasSecond = solutions.some((s) => s.billingMode === 'second')
  const hasLex = solutions.some((s) => s.billingMode === 'lex')
  const hasDigital = solutions.some((s) => s.billingMode === 'digital')
  const roundedMinuteSolutions = solutions.filter((s) => s.billingMode === 'roundedMinute')
  const roundedTotalMinuteSolutions = solutions.filter((s) => s.billingMode === 'roundedTotalMinute')
  const genesysVoiceTokenSolutions = solutions.filter((s) => s.billingMode === 'genesysVoiceToken')
  const genesysInteractionTokenSolutions = solutions.filter((s) => s.billingMode === 'genesysInteractionToken')
  const genesysSeatTokenSolutions = solutions.filter((s) => s.billingMode === 'genesysSeatToken')
  const engagementSolutions = solutions.filter((s) => s.billingMode === 'engagement')
  const hasMessageCredits = solutions.some((s) => s.billingMode === 'messageCredits')
  const hasPerMessage = solutions.some((s) => s.billingMode === 'perMessage')
  const hasCiscoSplit = solutions.some((s) => s.billingMode === 'ciscoSplit')
  const hasCiscoAssistantBlend = solutions.some((s) => s.billingMode === 'ciscoAssistantBlend')

  if (!hasSession && !hasMinute && !hasConcurrent && !hasSecond && !hasLex && !hasDigital && roundedMinuteSolutions.length === 0 && roundedTotalMinuteSolutions.length === 0 && genesysVoiceTokenSolutions.length === 0 && genesysInteractionTokenSolutions.length === 0 && genesysSeatTokenSolutions.length === 0 && engagementSolutions.length === 0 && !hasMessageCredits && !hasPerMessage && !hasCiscoSplit && !hasCiscoAssistantBlend) return null

  return (
    <div className="billing-notes">
      {hasSession && (
        <div className="billing-note session">
          <strong>Old model:</strong> voice sessions billed in {SESSION_BLOCK_SECONDS}-second ({SESSION_BLOCK_SECONDS / 60}-min) blocks, rounded up.
          A 150-sec call = 2 sessions.
        </div>
      )}
      {hasMinute && (
        <div className="billing-note minute">
          <strong>New model:</strong> voice usage billed continuously, metered to the second.
          A 150-sec call = 2.5 minutes consumed.
        </div>
      )}
      {hasConcurrent && (
        <div className="billing-note concurrent">
          <strong>Five9 (Old):</strong> priced per concurrent IVA session.
          Cost = peak concurrent calls × ${FIVE9_PRICE_PER_SESSION}/session.
        </div>
      )}
      {hasSecond && (
        <div className="billing-note second">
          <strong>Google:</strong> priced per second of voice usage.
          Cost = total calls × avg call length (sec) × price/sec.
        </div>
      )}
      {hasLex && (
        <div className="billing-note lex">
          <strong>Amazon Connect + Lex:</strong> Lex billed per speech request (${AMAZON_LEX_SPEECH_PRICE}).
          Connect charges shown separately — Basic: ${AMAZON_CONNECT_BASIC_PRICE_PER_MIN}/min · AI Bundled: ${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN}/min (no extra AI charges).
        </div>
      )}
      {genesysVoiceTokenSolutions.map((s) => (
        <div className="billing-note token" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> billed per token · {s.minutesPerToken} min/token, call length rounded up to the nearest 15 sec.
          Named: {GENESYS_FREE_TOKENS_NAMED} free tokens · Concurrent: {GENESYS_FREE_TOKENS_CONCURRENT} free tokens per period — shared org-wide across all Genesys AI products, shown here as if this were the only one in use.
        </div>
      ))}
      {genesysInteractionTokenSolutions.map((s) => (
        <div className="billing-note token" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> billed per token · {s.tokensPerInteraction.toLocaleString('en-US', { maximumFractionDigits: 4 })} tokens/interaction.
          Named: {GENESYS_FREE_TOKENS_NAMED} free tokens · Concurrent: {GENESYS_FREE_TOKENS_CONCURRENT} free tokens per period — shared org-wide across all Genesys AI products, shown here as if this were the only one in use.
        </div>
      ))}
      {genesysSeatTokenSolutions.map((s) => (
        <div className="billing-note token" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> billed per token, metered per agent seat rather than per interaction · {s.tokensPerSeatNamed} tokens/seat (Named) · {s.tokensPerSeatConcurrent} tokens/seat (Concurrent).
          Named: {GENESYS_FREE_TOKENS_NAMED} free tokens · Concurrent: {GENESYS_FREE_TOKENS_CONCURRENT} free tokens per period — shared org-wide across all Genesys AI products, shown here as if this were the only one in use.
        </div>
      ))}
      {hasDigital && (
        <div className="billing-note minute">
          <strong>Cisco Digital:</strong> billed per AI Agent Unit based on {DIGITAL_MESSAGES_PER_STANDARD_SESSION}-message session standard.
          Interactions with more or fewer messages are normalized proportionally.
          Scripted: {CISCO_DIGITAL_SCRIPTED_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit · Autonomous: {CISCO_DIGITAL_AUTONOMOUS_SESSIONS_PER_UNIT.toLocaleString()} sessions/unit.
        </div>
      )}
      {roundedMinuteSolutions.map((s) => (
        <div className="billing-note minute" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> priced per minute, rounded up to the nearest minute per call.
          Cost = total calls × ⌈avg call length ÷ 60⌉ × ${s.unitPrice}/min.
        </div>
      ))}
      {roundedTotalMinuteSolutions.map((s) => (
        <div className="billing-note minute" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> priced per minute on total monthly usage, rounded up once at the end (not per call).
          Cost = ⌈(total calls × avg call length ÷ 60)⌉ × ${s.unitPrice}/min.
        </div>
      ))}
      {engagementSolutions.map((s) => (
        <div className="billing-note digital" key={s.id}>
          <strong>{s.name} — {s.subtitle}:</strong> flat rate per engagement/session, no message-based proration.
          Cost = digital interactions × ${s.unitPrice}/engagement.
        </div>
      ))}
      {hasMessageCredits && (
        <div className="billing-note digital">
          <strong>Five9 Digital:</strong> billed in credit-pack units, no message normalization.
          Cost = ⌈(digital interactions × avg messages) ÷ {FIVE9_DIGITAL_CREDITS_PER_UNIT.toLocaleString()}⌉ × ${FIVE9_DIGITAL_LIST_PRICE}/unit — 1 credit per message sent or received.
        </div>
      )}
      {hasPerMessage && (
        <div className="billing-note digital">
          <strong>Amazon Connect Digital:</strong> flat rate per message, no bundling.
          Cost = digital interactions × avg messages × ${AMAZON_CONNECT_DIGITAL_PRICE_PER_MESSAGE}/message.
        </div>
      )}
      {hasCiscoSplit && (
        <div className="billing-note minute">
          <strong>Cisco Split Agent:</strong> total minutes split by Scripted/Autonomous %, each tier packed into its own unit size and summed.
          Scripted: {NEW_SCRIPTED_MINUTES_PER_UNIT.toLocaleString()} min/unit · Autonomous: {NEW_AUTONOMOUS_MINUTES_PER_UNIT.toLocaleString()} min/unit.
        </div>
      )}
      {hasCiscoAssistantBlend && (
        <div className="billing-note minute">
          <strong>Cisco AI Assistant:</strong> one shared unit pool covers voice minutes AND digital sessions, proportionally blended — not two separate pools.
          Cost = ⌈(voice minutes ÷ minutes/unit) + (digital sessions ÷ sessions/unit)⌉ × price/unit.
        </div>
      )}
    </div>
  )
}

const POOL_NAMES = { genesys: 'Genesys (shared token pool)' }

function lineGrossCost(r) {
  return r.billingMode === 'lex' ? r.totalCostWithoutConnect : r.totalCost
}

// Groups cart lines by solution, then rolls solutions sharing a `poolGroup`
// (currently only Genesys) into a pool-level gross → discount → net rollup,
// so the shared free-token allowance is applied once across the whole pool
// rather than independently per line or per solution. `applyDiscount=false`
// (used on the individual Agent/Assistants tabs) keeps `pools` around for
// display but sums every solution's gross subtotal into grandTotal untouched —
// the discount is only ever subtracted once, on the combined AI Cost Summary.
function buildCartSummary(cartLines, lineResults, licenseType, applyDiscount = true) {
  const bySolution = {}
  cartLines.forEach((line) => {
    const r = lineResults[line.id]
    if (!r) return
    const solution = SOLUTIONS[line.solutionId]
    bySolution[line.solutionId] ??= { solution, entries: [], subtotal: 0 }
    const cost = lineGrossCost(r)
    bySolution[line.solutionId].entries.push({ line, result: r, cost })
    bySolution[line.solutionId].subtotal += cost
  })

  const pools = {}
  Object.values(bySolution).forEach(({ solution, subtotal, entries }) => {
    if (!solution.poolGroup) return
    pools[solution.poolGroup] ??= { key: solution.poolGroup, grossTokens: 0, grossCost: 0, price: 0, solutionIds: [] }
    const p = pools[solution.poolGroup]
    p.grossCost += subtotal
    entries.forEach(({ result }) => { p.grossTokens += result.rawTokens ?? 0 })
    p.price = entries[0]?.result.effectiveUnitPrice ?? p.price
    if (!p.solutionIds.includes(solution.id)) p.solutionIds.push(solution.id)
  })
  Object.values(pools).forEach((p) => {
    const freeTokens = p.key === 'genesys'
      ? (licenseType === 'concurrent' ? GENESYS_FREE_TOKENS_CONCURRENT : GENESYS_FREE_TOKENS_NAMED)
      : 0
    const discountTokens = Math.min(freeTokens, p.grossTokens)
    p.freeTokens = freeTokens
    p.discountDollars = discountTokens * p.price
    p.netCost = Math.max(0, p.grossCost - p.discountDollars)
  })

  let grandTotal = 0
  const countedPools = new Set()
  Object.values(bySolution).forEach(({ solution, subtotal }) => {
    if (solution.poolGroup && applyDiscount) {
      if (!countedPools.has(solution.poolGroup)) {
        grandTotal += pools[solution.poolGroup].netCost
        countedPools.add(solution.poolGroup)
      }
    } else {
      grandTotal += subtotal
    }
  })

  return { bySolution, pools, grandTotal }
}

// A short, mode-appropriate usage figure for a result — "1,234 tokens", "6 units",
// "42 concurrent" — so a summary row reads as "what was consumed" alongside "what it cost".
function usageLabelFor(result) {
  switch (result.billingMode) {
    case 'genesysVoiceToken':
    case 'genesysInteractionToken':
    case 'genesysSeatToken':
      return `${result.rawTokens.toLocaleString('en-US', { maximumFractionDigits: 2 })} tokens`
    case 'minute':
    case 'digital':
    case 'messageCredits':
    case 'ciscoSplit':
    case 'ciscoAssistantBlend':
    case 'session':
      return `${result.unitsRequired.toLocaleString()} units`
    case 'roundedMinute':
    case 'roundedTotalMinute':
      return `${result.totalMinutes.toLocaleString()} min`
    case 'concurrent':
      return `${result.peakConcurrent.toLocaleString()} concurrent`
    case 'second':
      return `${result.totalSeconds.toLocaleString('en-US', { maximumFractionDigits: 0 })} sec`
    case 'lex':
      return `${(result.lexSpeechCost / result.effectiveSpeechPrice).toLocaleString('en-US', { maximumFractionDigits: 0 })} speech req`
    case 'engagement':
      return `${result.numInteractions.toLocaleString()} engagements`
    case 'perMessage':
      return `${Math.ceil(result.totalMessages).toLocaleString()} messages`
    default:
      return ''
  }
}

function CartSummary({ cartLines, lineResults, licenseType, showDiscount = true }) {
  if (cartLines.length === 0) return null
  const { bySolution, pools, grandTotal } = buildCartSummary(cartLines, lineResults, licenseType, showDiscount)
  const solutionGroups = Object.values(bySolution)
  if (solutionGroups.length === 0) return null

  const fmt2 = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const hasPoolGroup = solutionGroups.some((g) => g.solution.poolGroup)

  return (
    <div className="cart-summary-wrap">
      <h3 className="scenario-breakdown-title">Cart Summary</h3>

      {solutionGroups.map(({ solution, entries, subtotal }) => (
        <div className="cart-summary-group" key={solution.id}>
          <div className="cart-summary-group-header" style={{ borderTopColor: solution.accentColor }}>
            <span className="table-head-name">{solution.name}</span>
            <span className="table-head-subtitle" style={{ color: solution.accentColor }}>{solution.subtitle}</span>
          </div>
          <table className="comparison-table cart-summary-table">
            <tbody>
              {entries.map(({ line, result, cost }, i) => (
                <tr key={line.id}>
                  <td className="table-row-label">
                    {line.label || `Line ${i + 1}`}
                    {usageLabelFor(result) && <span className="table-usage-note"> · {usageLabelFor(result)}</span>}
                  </td>
                  <td>
                    {fmt2(cost)}
                    {result.billingMode === 'lex' && (
                      <span className="table-secondary-cost"> ({fmt2(result.totalCostWithConnect)} w/ Connect)</span>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="table-highlight-row">
                <td className="table-row-label">Total</td>
                <td>{fmt2(subtotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}

      {showDiscount && Object.values(pools).length > 0 && (
        <div className="cart-summary-discounts">
          {Object.values(pools).map((p) => (
            <div className="cart-summary-discount-line" key={p.key}>
              <span>Discount — {POOL_NAMES[p.key] ?? p.key} ({p.freeTokens.toLocaleString()} free tokens, {licenseType})</span>
              <span>-{fmt2(p.discountDollars)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="cart-summary-grand-total">
        <span>{showDiscount ? 'Overall Bill' : 'Gross Total (undiscounted)'}</span>
        <span>{fmt2(grandTotal)}</span>
      </div>
      {!showDiscount && hasPoolGroup && (
        <p className="section-desc" style={{ marginTop: 8 }}>
          Includes gross Genesys token costs before the shared free-tier discount — see the AI Cost Summary tab for the discounted total.
        </p>
      )}
    </div>
  )
}

// ─── ROI Line Chart ───────────────────────────────────────────────────

function RoiLineChart({ rows }) {
  if (!rows || rows.length < 2) return null
  const hasConservative = rows.some(r => r.conservative !== null)
  const hasAggressive = rows.some(r => r.aggressive !== null)
  const W = 800, H = 280
  const pL = 75, pR = 24, pT = 30, pB = 36
  const iW = W - pL - pR, iH = H - pT - pB
  const n = rows.length
  const allVals = rows.flatMap(r => {
    const v = [r.cumulativeCurrent, r.expected.cumulativeAI]
    if (r.conservative) v.push(r.conservative.cumulativeAI)
    if (r.aggressive) v.push(r.aggressive.cumulativeAI)
    return v
  })
  const yMax = Math.max(...allVals) * 1.05
  const xOf = (m) => pL + ((m - 1) / (n - 1)) * iW
  const yOf = (v) => pT + iH - (v / yMax) * iH
  const makePath = (vals) => vals.map((v, i) => `${i === 0 ? 'M' : 'L'}${xOf(i + 1).toFixed(1)},${yOf(v).toFixed(1)}`).join(' ')
  const fmt = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}K` : `$${v}`
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => yMax * f)
  const step = Math.ceil(n / 6)
  const xTickMonths = rows.filter((r, i) => i === 0 || (i + 1) % step === 0 || i === n - 1).map(r => r.month)
  const COLORS = { current: '#ff6b6b', conservative: '#74c0fc', expected: '#69db7c', aggressive: '#ffa94d' }
  const legendItems = [
    { label: 'Current Cost', color: COLORS.current, dash: false },
    hasConservative && { label: 'Conservative', color: COLORS.conservative, dash: true },
    { label: 'Expected', color: COLORS.expected, dash: false },
    hasAggressive && { label: 'Aggressive', color: COLORS.aggressive, dash: true },
  ].filter(Boolean)
  return (
    <div className="roi-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={pL} y1={yOf(v)} x2={W - pR} y2={yOf(v)} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
            <text x={pL - 6} y={yOf(v) + 4} textAnchor="end" fill="rgba(255,255,255,0.45)" fontSize="11">{fmt(v)}</text>
          </g>
        ))}
        {xTickMonths.map(m => (
          <text key={m} x={xOf(m)} y={H - pB + 14} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="11">Mo {m}</text>
        ))}
        <path d={makePath(rows.map(r => r.cumulativeCurrent))} fill="none" stroke={COLORS.current} strokeWidth="2.5" />
        {hasConservative && <path d={makePath(rows.map(r => r.conservative.cumulativeAI))} fill="none" stroke={COLORS.conservative} strokeWidth="2" strokeDasharray="5,3" />}
        <path d={makePath(rows.map(r => r.expected.cumulativeAI))} fill="none" stroke={COLORS.expected} strokeWidth="2.5" />
        {hasAggressive && <path d={makePath(rows.map(r => r.aggressive.cumulativeAI))} fill="none" stroke={COLORS.aggressive} strokeWidth="2" strokeDasharray="5,3" />}
        {legendItems.map((item, i) => (
          <g key={i} transform={`translate(${pL + i * 132}, ${pT - 16})`}>
            <line x1="0" y1="0" x2="22" y2="0" stroke={item.color} strokeWidth="2.5" strokeDasharray={item.dash ? '5,3' : undefined} />
            <text x="26" y="4" fill="rgba(255,255,255,0.75)" fontSize="11">{item.label}</text>
          </g>
        ))}
      </svg>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────

const VOLUME_MODE_DIGITAL = ['digital', 'engagement', 'messageCredits', 'perMessage', 'genesysInteractionToken']
const VOLUME_MODE_MESSAGE_BASED = ['digital', 'messageCredits', 'perMessage']

function overrideKeyFor(solution) {
  return solution.pricingGroup ?? solution.id
}

function emptyVolumeFields() {
  return {
    numCalls: '', avgCallLength: '',
    peakConcurrent: '',
    numDigitalInteractions: '', avgMessagesPerSession: '10',
    splitTotalSeconds: '', splitScriptedPct: '',
    numSeats: '',
    blendVoiceMinutes: '', blendDigitalSessions: '',
  }
}

function newCartLine(id, solutionId) {
  return { id, solutionId, label: '', ...emptyVolumeFields() }
}

function newLob(id) {
  return { id, label: '', volumes: {}, excludedSolutionIds: [] }
}

// Solutions a user has removed from this one LOB (but not from the global
// palette) are skipped when fanning out — they stay excluded until re-added
// via that LOB's own "+ Add Service" row.
function flattenLobs(lobs, selectedIds) {
  const out = []
  lobs.forEach((lob, lobIndex) => {
    const label = lob.label || `Line of Business ${lobIndex + 1}`
    selectedIds.filter((id) => !lob.excludedSolutionIds.includes(id)).forEach((solutionId) => {
      const vol = lob.volumes[solutionId] ?? emptyVolumeFields()
      out.push({ id: `${lob.id}:${solutionId}`, solutionId, label, lobIndex, ...vol })
    })
  })
  return out
}

function emptyResultsFor(solution) {
  return {
    billingMode: solution.billingMode,
    sessionsPerCall: 0, totalSessions: 0, totalMinutes: 0,
    peakConcurrent: 0, unitsRequired: 0, totalCost: 0,
    effectiveUnitPrice: 0, listPrice: 0, numCalls: 0, avgCallLengthSecs: 0,
    numSpeechRequests: 0, lexSpeechCost: 0,
    connectTotalCost: 0,
    totalCostWithoutConnect: 0, totalCostWithConnect: 0,
    effectiveSpeechPrice: 0, effectiveConnectPrice: 0,
    rawTokens: 0,
    normalizedSessions: 0, numInteractions: 0, avgMsgs: 10,
    numSeats: 0, tokensPerSeat: 0,
    blendVoiceMinutes: 0, blendDigitalSessions: 0, voiceUnits: 0, digitalUnits: 0,
  }
}

function CartLineVolumeFields({ line, mode, onChange }) {
  if (mode === 'concurrent') {
    return (
      <InputField
        label="Peak Concurrent Calls" hint="Maximum simultaneous calls at peak"
        value={line.peakConcurrent} onChange={(v) => onChange('peakConcurrent', v)} min={1}
      />
    )
  }
  if (mode === 'ciscoSplit') {
    return (
      <>
        <InputField
          label="Total Seconds" hint="Total blended usage in seconds"
          value={line.splitTotalSeconds} onChange={(v) => onChange('splitTotalSeconds', v)} min={1} suffix="sec"
        />
        <InputField
          label="Scripted %" hint="Percent Scripted; remainder is billed Autonomous"
          value={line.splitScriptedPct} onChange={(v) => onChange('splitScriptedPct', v)} min={0} max={100} suffix="%"
        />
      </>
    )
  }
  if (mode === 'genesysInteractionToken') {
    return (
      <InputField
        label="Interactions" hint="Total interactions this feature applies to — evaluations, translations, summaries, or posts, depending on the solution"
        value={line.numDigitalInteractions} onChange={(v) => onChange('numDigitalInteractions', v)} min={1}
      />
    )
  }
  if (mode === 'genesysSeatToken') {
    return (
      <InputField
        label="Agent Seats" hint="Number of agent seats using this feature — consumption depends on the License Type (Named/Concurrent) set above"
        value={line.numSeats} onChange={(v) => onChange('numSeats', v)} min={1}
      />
    )
  }
  if (mode === 'ciscoAssistantBlend') {
    return (
      <>
        <InputField
          label="Voice Minutes" hint="Total voice minutes consumed this period"
          value={line.blendVoiceMinutes} onChange={(v) => onChange('blendVoiceMinutes', v)} min={0}
        />
        <InputField
          label="Digital Sessions" hint="Total digital sessions (10-message standard)"
          value={line.blendDigitalSessions} onChange={(v) => onChange('blendDigitalSessions', v)} min={0}
        />
      </>
    )
  }
  if (VOLUME_MODE_DIGITAL.includes(mode)) {
    return (
      <>
        <InputField
          label="Digital Interactions" hint="Total digital (chat/messaging) interactions"
          value={line.numDigitalInteractions} onChange={(v) => onChange('numDigitalInteractions', v)} min={1}
        />
        {VOLUME_MODE_MESSAGE_BASED.includes(mode) && (
          <InputField
            label="Avg Messages/Session" hint="Used by message-based billing models"
            value={line.avgMessagesPerSession} onChange={(v) => onChange('avgMessagesPerSession', v)} min={1}
          />
        )}
      </>
    )
  }
  return (
    <>
      <InputField
        label="Number of Calls" hint="Total calls in billing period"
        value={line.numCalls} onChange={(v) => onChange('numCalls', v)} min={1}
      />
      <InputField
        label="Avg Call Length" hint="Mean duration per call in seconds"
        value={line.avgCallLength} onChange={(v) => onChange('avgCallLength', v)} min={1} step={1} suffix="sec"
      />
    </>
  )
}

function CalculatorTab({ solutionOrder, defaultSelectedIds, licenseType, setLicenseType, genesysPriceOverride, setGenesysPriceOverride, lobMode = false, onLinesChange }) {
  const [selectedIds, setSelectedIds] = useState(defaultSelectedIds)
  const [cartLines, setCartLines] = useState(!lobMode && defaultSelectedIds[0] ? [newCartLine(1, defaultSelectedIds[0])] : [])
  const [nextLineId, setNextLineId] = useState(2)
  const [lobs, setLobs] = useState(lobMode && defaultSelectedIds.length ? [newLob(1)] : [])
  const [nextLobId, setNextLobId] = useState(2)
  const [pricingOverrides, setPricingOverrides] = useState({})
  const [amazonSpeechReqsPerCall, setAmazonSpeechReqsPerCall] = useState('')
  const [solutionsOpen, setSolutionsOpen] = useState(true)
  const [pricingOpen, setPricingOpen] = useState(true)
  const [cartOpen, setCartOpen] = useState(true)

  function lineIsReady(line) {
    const mode = SOLUTIONS[line.solutionId]?.billingMode
    if (!mode) return false
    if (mode === 'concurrent') return Number(line.peakConcurrent) > 0
    if (mode === 'ciscoSplit') {
      return Number(line.splitTotalSeconds) > 0 && line.splitScriptedPct !== '' &&
        Number(line.splitScriptedPct) >= 0 && Number(line.splitScriptedPct) <= 100
    }
    if (mode === 'genesysSeatToken') return Number(line.numSeats) > 0
    if (mode === 'ciscoAssistantBlend') return Number(line.blendVoiceMinutes) > 0 || Number(line.blendDigitalSessions) > 0
    if (VOLUME_MODE_DIGITAL.includes(mode)) return Number(line.numDigitalInteractions) > 0
    return Number(line.numCalls) > 0 && Number(line.avgCallLength) > 0
  }

  function addLine() {
    if (!selectedIds.length) return // nothing in the palette yet — checkbox a solution above first
    setCartLines(prev => [...prev, newCartLine(nextLineId, selectedIds[0])])
    setNextLineId(n => n + 1)
  }

  function removeLine(id) {
    setCartLines(prev => prev.filter(l => l.id !== id))
  }

  // In LOB mode, `id` is the composite flat id `${lob.id}:${solutionId}` produced
  // by flattenLobs() — parsed back apart to update the right nested volume field.
  function updateLine(id, field, value) {
    if (lobMode) {
      const sep = id.indexOf(':')
      const lobId = Number(id.slice(0, sep))
      const solutionId = id.slice(sep + 1)
      setLobs(prev => prev.map(l => l.id !== lobId ? l : {
        ...l,
        volumes: { ...l.volumes, [solutionId]: { ...(l.volumes[solutionId] ?? emptyVolumeFields()), [field]: value } },
      }))
      return
    }
    setCartLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l))
  }

  function addLob() {
    if (!selectedIds.length) return // nothing in the palette yet — checkbox a solution above first
    setLobs(prev => [...prev, newLob(nextLobId)])
    setNextLobId(n => n + 1)
  }

  function removeLob(lobId) {
    setLobs(prev => prev.filter(l => l.id !== lobId))
  }

  function updateLobLabel(lobId, label) {
    setLobs(prev => prev.map(l => l.id === lobId ? { ...l, label } : l))
  }

  // Removing a service from a LOB only hides it for that one LOB (excludedSolutionIds) —
  // it doesn't touch the global palette or the other LOBs, and any volume already entered
  // for it is kept, so re-adding it via "+ Add Service" brings the old numbers back.
  function toggleLobService(lobId, solutionId, exclude) {
    setLobs(prev => prev.map(l => l.id !== lobId ? l : {
      ...l,
      excludedSolutionIds: exclude
        ? [...l.excludedSolutionIds, solutionId]
        : l.excludedSolutionIds.filter((id) => id !== solutionId),
    }))
  }

  function toggleSolution(id) {
    const isSelected = selectedIds.includes(id)
    if (!isSelected) {
      setSelectedIds(prev => [...prev, id].sort((a, b) => solutionOrder.indexOf(a) - solutionOrder.indexOf(b)))
      return
    }
    setSelectedIds(prev => prev.filter(s => s !== id))
    if (!lobMode) setCartLines(prev => prev.filter(l => l.solutionId !== id))
  }

  const vendors = useMemo(() => {
    const seen = new Set()
    const list = []
    solutionOrder.forEach((id) => {
      const vendor = SOLUTIONS[id].name.split(' ')[0]
      if (!seen.has(vendor)) { seen.add(vendor); list.push(vendor) }
    })
    return list
  }, [solutionOrder])

  function vendorIdsFor(vendor) {
    return solutionOrder.filter((id) => SOLUTIONS[id].name.split(' ')[0] === vendor)
  }

  function toggleVendor(vendor) {
    const vendorIds = vendorIdsFor(vendor)
    const allSelected = vendorIds.every((id) => selectedIds.includes(id))
    if (allSelected) {
      const remaining = selectedIds.filter((id) => !vendorIds.includes(id))
      setSelectedIds(remaining)
      if (!lobMode) setCartLines((prev) => prev.filter((l) => !vendorIds.includes(l.solutionId)))
      return
    }
    setSelectedIds((prev) => {
      const merged = new Set([...prev, ...vendorIds])
      return solutionOrder.filter((id) => merged.has(id))
    })
  }

  // The Genesys $/token override and Named/Concurrent license type are shared
  // (lifted to the parent AiCalculatorApp) across both the AI Agent and AI
  // Assistants tabs — they're the same real-world negotiated rate and org-wide
  // license type regardless of which Genesys product is being priced. Every
  // other override stays local to this tab's own `pricingOverrides` map.
  function overrideValue(key) {
    return key === 'genesys' ? genesysPriceOverride : pricingOverrides[key]
  }

  function getOverridePrice(solution) {
    const key = overrideKeyFor(solution)
    return Number(key === 'genesys' ? genesysPriceOverride : pricingOverrides[key]) || 0
  }

  function getOverrideConnectPrice(solution) {
    if (!solution.connectOverrideKey) return 0
    return Number(pricingOverrides[solution.connectOverrideKey]) || 0
  }

  function setOverride(key, value) {
    if (key === 'genesys') { setGenesysPriceOverride(value); return }
    setPricingOverrides(prev => ({ ...prev, [key]: value }))
  }

  const overrideGroups = useMemo(() => {
    const seen = new Map()
    selectedIds.forEach((id) => {
      const s = SOLUTIONS[id]
      const key = overrideKeyFor(s)
      if (seen.has(key)) return
      seen.set(key, {
        key,
        label: s.pricingGroup ? `${s.name} (shared price)` : s.subtitle ? `${s.name} — ${s.subtitle}` : s.name,
        listPrice: s.unitPrice ?? s.listPrice,
        step: ['second', 'lex'].includes(s.billingMode) ? 0.0001 : 0.01,
      })
    })
    return Array.from(seen.values())
  }, [selectedIds])

  // A flat list of lines regardless of cart mode — one entry per cart line in
  // classic mode, or one entry per (Line of Business × selected solution) in LOB
  // mode. Everything downstream (lineResults, CartSummary, ResultCard, the Peak
  // Concurrent section) reads this instead of `cartLines`/`lobs` directly.
  const effectiveLines = useMemo(
    () => lobMode ? flattenLobs(lobs, selectedIds) : cartLines,
    [lobMode, lobs, cartLines, selectedIds]
  )

  const lineResults = useMemo(() => {
    const map = {}
    effectiveLines.forEach((line) => {
      const solution = SOLUTIONS[line.solutionId]
      if (!solution || !lineIsReady(line)) return
      const overridePrice = getOverridePrice(solution)
      const overrideConnectPrice = getOverrideConnectPrice(solution)
      if (solution.billingMode === 'concurrent') {
        map[line.id] = calculateResults({
          numCalls: 0, avgCallLength: 0, peakConcurrent: Number(line.peakConcurrent),
          numSpeechRequests: 0, solution, overridePrice, overrideConnectPrice,
        })
        return
      }
      if (VOLUME_MODE_DIGITAL.includes(solution.billingMode)) {
        map[line.id] = calculateResults({
          solution,
          numDigitalInteractions: Number(line.numDigitalInteractions),
          avgMessagesPerSession: Number(line.avgMessagesPerSession),
          overridePrice,
        })
        return
      }
      if (solution.billingMode === 'ciscoSplit') {
        map[line.id] = calculateResults({
          solution,
          splitTotalSeconds: Number(line.splitTotalSeconds),
          splitScriptedPct: Number(line.splitScriptedPct),
          overridePrice,
        })
        return
      }
      if (solution.billingMode === 'genesysSeatToken') {
        map[line.id] = calculateResults({
          solution,
          numSeats: Number(line.numSeats),
          licenseType,
          overridePrice,
        })
        return
      }
      if (solution.billingMode === 'ciscoAssistantBlend') {
        map[line.id] = calculateResults({
          solution,
          blendVoiceMinutes: Number(line.blendVoiceMinutes),
          blendDigitalSessions: Number(line.blendDigitalSessions),
          overridePrice,
        })
        return
      }
      const numSpeechRequests = amazonSpeechReqsPerCall !== ''
        ? Number(amazonSpeechReqsPerCall)
        : (Number(line.avgCallLength) / 60) * 4
      map[line.id] = calculateResults({
        numCalls: Number(line.numCalls), avgCallLength: Number(line.avgCallLength),
        peakConcurrent: 0, numSpeechRequests, solution,
        overridePrice, overrideConnectPrice,
      })
    })
    return map
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveLines, pricingOverrides, amazonSpeechReqsPerCall, licenseType, genesysPriceOverride])

  // Reports this tab's raw lines + results up to the parent AiCalculatorApp,
  // which combines both tabs' lines into one buildCartSummary() call for the AI
  // Cost Summary tab — that's the only place a discount is ever applied now;
  // each tab's own CartSummary above renders with showDiscount={false}.
  useEffect(() => {
    onLinesChange?.({ cartLines: effectiveLines, lineResults })
  }, [effectiveLines, lineResults, onLinesChange])

  return (
    <main className="app-main">
          <section className="results-section">
            <button className="section-toggle" onClick={() => setSolutionsOpen((o) => !o)} aria-expanded={solutionsOpen}>
              <h2>Solution Comparison</h2>
              <span className={`section-chevron${solutionsOpen ? ' open' : ''}`}>&#8964;</span>
            </button>
            {solutionsOpen && (
              <>
                <p className="section-desc" style={{ marginBottom: 12 }}>Pick every solution you want available to price out below — this is the palette your cart lines draw from.</p>

                <div className="vendor-quickselect">
                  {vendors.map((v) => {
                    const vendorIds = vendorIdsFor(v)
                    const allSelected = vendorIds.every((id) => selectedIds.includes(id))
                    return (
                      <button
                        key={v} type="button"
                        className={`vendor-quickselect-btn${allSelected ? ' selected' : ''}`}
                        onClick={() => toggleVendor(v)}
                      >
                        {allSelected ? '−' : '+'} All {v}
                      </button>
                    )
                  })}
                </div>

                <div className="solution-checkboxes">
                  {solutionOrder.map((id) => {
                    const s = SOLUTIONS[id]
                    const checked = selectedIds.includes(id)
                    return (
                      <label
                        key={id}
                        className={`solution-checkbox-item${checked ? ' checked' : ''}`}
                        style={{ '--accent': s.accentColor }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSolution(id)}
                        />
                        <span className="checkbox-solution-name">{s.name}</span>
                        {s.subtitle && (
                          <span className="checkbox-solution-subtitle" style={{ color: checked ? s.accentColor : undefined }}>
                            {s.subtitle}
                          </span>
                        )}
                      </label>
                    )
                  })}
                </div>

                <BillingNote comparisonIds={selectedIds} />
              </>
            )}
          </section>

          <section className="inputs-section">
            <button className="section-toggle" onClick={() => setPricingOpen((o) => !o)} aria-expanded={pricingOpen}>
              <h2>Pricing Considerations</h2>
              <span className={`section-chevron${pricingOpen ? ' open' : ''}`}>&#8964;</span>
            </button>
            {pricingOpen && <div className="inputs-grid">
              <div className="input-field">
                <label>Agent License Type</label>
                <span className="input-hint">Licensing model for this deployment — sets Genesys's free monthly token allowance. Shared across the AI Agent and AI Assistants tabs.</span>
                <div className="radio-toggle">
                  {[{ value: 'named', label: 'Named' }, { value: 'concurrent', label: 'Concurrent' }].map(({ value, label }) => (
                    <label key={value} className={`radio-toggle-option${licenseType === value ? ' selected' : ''}`}>
                      <input
                        type="radio"
                        name="licenseType"
                        value={value}
                        checked={licenseType === value}
                        onChange={() => setLicenseType(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              {overrideGroups.map((g) => (
                <div className="input-field customer-price-field" key={g.key}>
                  <label>{g.label} — per unit</label>
                  <span className="input-hint">
                    Overrides the ${g.listPrice} list price for every cart line using this solution
                    {g.key === 'genesys' && ' — shared across the AI Agent and AI Assistants tabs'}
                  </span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={overrideValue(g.key) ?? ''}
                      min={0}
                      step={g.step}
                      placeholder={`${g.listPrice} (list)`}
                      onChange={(e) => setOverride(g.key, e.target.value)}
                    />
                  </div>
                </div>
              ))}
              {selectedIds.some((id) => SOLUTIONS[id].connectOverrideKey) && (
                <div className="input-field amazon-price-field">
                  <label>Amazon Connect per Minute</label>
                  <span className="input-hint">Overrides Connect's per-minute charge — separate from the Lex per-speech-request rate above, since Lex bills two different unit types</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={pricingOverrides['amazon_connect_lex_min'] ?? ''}
                      min={0}
                      step={0.0001}
                      placeholder={`Basic ${AMAZON_CONNECT_BASIC_PRICE_PER_MIN} / Bundled ${AMAZON_CONNECT_AI_BUNDLED_PRICE_PER_MIN} (list)`}
                      onChange={(e) => setOverride('amazon_connect_lex_min', e.target.value)}
                    />
                  </div>
                </div>
              )}
              {selectedIds.some((id) => SOLUTIONS[id].billingMode === 'lex') && (
                <div className="input-field amazon-speech-field">
                  <label>Amazon Lex — Speech Requests per Call</label>
                  <span className="input-hint">Default: 4 per minute of call length. Override sets a fixed total per call.</span>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      value={amazonSpeechReqsPerCall}
                      min={0}
                      step={1}
                      placeholder="4/min (default)"
                      onChange={(e) => setAmazonSpeechReqsPerCall(e.target.value)}
                    />
                    <span className="input-adornment suffix">req</span>
                  </div>
                </div>
              )}
            </div>}
          </section>

          <section className="inputs-section">
            <button className="section-toggle" onClick={() => setCartOpen((o) => !o)} aria-expanded={cartOpen}>
              <h2>{lobMode ? 'Lines of Business' : 'Cart Lines'}</h2>
              <span className={`section-chevron${cartOpen ? ' open' : ''}`}>&#8964;</span>
            </button>
            {cartOpen && (selectedIds.length === 0 ? (
              <div className="placeholder-msg">
                Check at least one solution in Solution Comparison above to start building your {lobMode ? 'lines of business' : 'cart'}.
              </div>
            ) : (
              <div className="call-volume-content">
                <div className="scenarios-list">
                  {lobMode ? lobs.map((lob, i) => (
                    <div key={lob.id} className="scenario-row lob-block">
                      <div className="scenario-row-header">
                        <span className="scenario-label">Line of Business {i + 1}</span>
                        {lobs.length > 1 && (
                          <button className="scenario-remove-btn" onClick={() => removeLob(lob.id)} aria-label="Remove line of business">✕</button>
                        )}
                      </div>
                      <div className="input-field">
                        <label>Line of Business Name</label>
                        <span className="input-hint">Optional — shown in the Cart Summary below</span>
                        <div className="input-wrapper">
                          <input
                            type="text"
                            value={lob.label}
                            placeholder={`Line of Business ${i + 1}`}
                            onChange={(e) => updateLobLabel(lob.id, e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="lob-solution-rows">
                        {selectedIds.filter((id) => !lob.excludedSolutionIds.includes(id)).map((solutionId) => {
                          const solution = SOLUTIONS[solutionId]
                          const vol = lob.volumes[solutionId] ?? emptyVolumeFields()
                          const flatId = `${lob.id}:${solutionId}`
                          return (
                            <div key={solutionId} className="lob-solution-row" style={{ '--accent': solution.accentColor }}>
                              <div className="lob-solution-row-header">
                                <div className="lob-solution-row-name">
                                  {solution.name}{solution.subtitle ? ` — ${solution.subtitle}` : ''}
                                </div>
                                <button
                                  className="scenario-remove-btn"
                                  onClick={() => toggleLobService(lob.id, solutionId, true)}
                                  aria-label={`Remove ${solution.name} from this line of business`}
                                >✕</button>
                              </div>
                              <div className="lob-solution-row-inputs">
                                <CartLineVolumeFields
                                  line={vol}
                                  mode={solution.billingMode}
                                  onChange={(field, value) => updateLine(flatId, field, value)}
                                />
                              </div>
                            </div>
                          )
                        })}
                      </div>
                      {lob.excludedSolutionIds.some((id) => selectedIds.includes(id)) && (
                        <div className="vendor-quickselect lob-add-service-row">
                          {lob.excludedSolutionIds.filter((id) => selectedIds.includes(id)).map((id) => {
                            const s = SOLUTIONS[id]
                            return (
                              <button
                                key={id} type="button" className="vendor-quickselect-btn"
                                onClick={() => toggleLobService(lob.id, id, false)}
                              >
                                + {s.name}{s.subtitle ? ` — ${s.subtitle}` : ''}
                              </button>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )) : cartLines.map((line, i) => {
                    const solution = SOLUTIONS[line.solutionId]
                    return (
                      <div key={line.id} className="scenario-row">
                        <div className="scenario-row-header">
                          <span className="scenario-label">Line {i + 1}</span>
                          {cartLines.length > 1 && (
                            <button className="scenario-remove-btn" onClick={() => removeLine(line.id)} aria-label="Remove line">✕</button>
                          )}
                        </div>
                        <div className="scenario-row-inputs">
                          <div className="input-field">
                            <label>Solution</label>
                            <span className="input-hint">Which solution this line's volume prices out</span>
                            <div className="input-wrapper">
                              <select value={line.solutionId} onChange={(e) => updateLine(line.id, 'solutionId', e.target.value)}>
                                {selectedIds.map((id) => (
                                  <option key={id} value={id}>{SOLUTIONS[id].subtitle ? `${SOLUTIONS[id].name} — ${SOLUTIONS[id].subtitle}` : SOLUTIONS[id].name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <div className="input-field">
                            <label>Label</label>
                            <span className="input-hint">Optional — shown in the Cart Summary below</span>
                            <div className="input-wrapper">
                              <input
                                type="text"
                                value={line.label}
                                placeholder={`Line ${i + 1}`}
                                onChange={(e) => updateLine(line.id, 'label', e.target.value)}
                              />
                            </div>
                          </div>
                          {solution && (
                            <CartLineVolumeFields
                              line={line}
                              mode={solution.billingMode}
                              onChange={(field, value) => updateLine(line.id, field, value)}
                            />
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
                <button className="scenario-add-btn" onClick={lobMode ? addLob : addLine}>
                  {lobMode ? '+ Add Line of Business' : '+ Add Line'}
                </button>
              </div>
            ))}
          </section>

          <section className="results-section">
            <div className="results-header">
              <h2>Results</h2>
            </div>

            <div className="results-grid">
              {/* LOB mode fans each line of business out across every selected solution, so most
                  combos are left blank on purpose (not every LOB uses every solution) — only show
                  the ones actually filled in, rather than a wall of "enter volume" placeholders. */}
              {(lobMode ? effectiveLines.filter((l) => lineIsReady(l)) : effectiveLines).map((line, i) => {
                const solution = SOLUTIONS[line.solutionId]
                if (!solution) return null
                return (
                  <ResultCard
                    key={line.id}
                    solution={solution}
                    results={lineResults[line.id] ?? emptyResultsFor(solution)}
                    isReady={lineIsReady(line)}
                    lineLabel={line.label || `Line ${i + 1}`}
                  />
                )
              })}
            </div>

            <CartSummary cartLines={effectiveLines} lineResults={lineResults} licenseType={licenseType} showDiscount={false} />
          </section>

          {effectiveLines.filter((l) => SOLUTIONS[l.solutionId]?.billingMode === 'concurrent' && lineIsReady(l)).map((l) => (
            <section className="peak-section" key={l.id}>
              <div className="peak-card">
                <div className="peak-icon">⚡</div>
                <div>
                  <div className="peak-title">Peak Concurrent Capacity — {l.label || 'Five9 (Old)'}</div>
                  <div className="peak-value">{Number(l.peakConcurrent).toLocaleString()} simultaneous calls</div>
                  <div className="peak-note">
                    Ensure your telephony infrastructure (e.g. SBC ports, CUBE sessions) supports this peak load
                    in addition to the AI Agent Unit licensing above.
                  </div>
                </div>
              </div>
            </section>
          ))}

          <footer className="app-footer">
            <p>
              Prices shown are <strong>list price estimates</strong> unless a customer price is entered.
              Contact your Cisco partner or account team for discounted pricing.
            </p>
          </footer>
    </main>
  )
}

// High-level roll-up across both calculator tabs — 3 headline stats plus the
// full per-solution line-item breakdown (reusing CartSummary), so this is the
// one place the Genesys discount actually gets applied, computed once across
// every Genesys line from both tabs combined rather than per-tab.
function CostSummaryTab({ combinedLines, licenseType }) {
  const fmt2 = (n) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const { bySolution, pools, grandTotal } = useMemo(
    () => buildCartSummary(combinedLines.cartLines, combinedLines.lineResults, licenseType, true),
    [combinedLines, licenseType]
  )
  const totalUnits = useMemo(() => {
    let sum = 0
    Object.values(bySolution).forEach(({ entries }) => {
      entries.forEach(({ result }) => {
        if (typeof result.unitsRequired === 'number') sum += result.unitsRequired
      })
    })
    return sum
  }, [bySolution])
  const totalTokens = pools.genesys?.grossTokens ?? 0
  const hasLines = Object.keys(bySolution).length > 0

  return (
    <main className="app-main">
      <section className="results-section">
        <div className="results-header">
          <h2>AI Cost Summary</h2>
          <p className="section-desc">
            High-level roll-up across the AI Agent and AI Assistants calculators — configure volumes on those tabs first.
          </p>
        </div>

        <div className="cost-summary-stats">
          <div className="cost-summary-stat cost-summary-stat-primary">
            <div className="cost-summary-stat-label">Total Monthly Cost</div>
            <div className="cost-summary-stat-value">{fmt2(grandTotal)}</div>
          </div>
          <div className="cost-summary-stat">
            <div className="cost-summary-stat-label">Total AI Units</div>
            <div className="cost-summary-stat-value">{totalUnits.toLocaleString()}</div>
            <div className="cost-summary-stat-note">License units — Cisco AI Agent / AI Assistant &amp; Five9 Digital</div>
          </div>
          <div className="cost-summary-stat">
            <div className="cost-summary-stat-label">Total Genesys Tokens</div>
            <div className="cost-summary-stat-value">{totalTokens.toLocaleString('en-US', { maximumFractionDigits: 1 })}</div>
            <div className="cost-summary-stat-note">Gross usage across both tabs, before the free-tier discount</div>
          </div>
        </div>

        {hasLines ? (
          <CartSummary cartLines={combinedLines.cartLines} lineResults={combinedLines.lineResults} licenseType={licenseType} showDiscount />
        ) : (
          <div className="placeholder-msg">No usage configured yet on either calculator tab.</div>
        )}
      </section>
    </main>
  )
}

function RoiTab({ roiRows, roiCurrentCost, setRoiCurrentCost, roiPS, setRoiPS, roiMonthlyUsage, setRoiMonthlyUsage, roiMonthlyConservative, setRoiMonthlyConservative, roiMonthlyAggressive, setRoiMonthlyAggressive, roiTimeline, setRoiTimeline }) {
  return (
          <main className="app-main">
            <section className="inputs-section">
              <div className="section-header-static">
                <h2>ROI Break-Even Calculator</h2>
                <p className="section-desc">Compare your current monthly costs against AI investment to find when the solution pays for itself.</p>
              </div>
              <div className="inputs-grid" style={{ marginBottom: '20px' }}>
                <div className="input-field">
                  <label>Current Monthly Cost of Doing Business</label>
                  <span className="input-hint">What you spend per month today (before AI)</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiCurrentCost}
                      min={0}
                      step={1}
                      placeholder="e.g. 50000"
                      onChange={(e) => setRoiCurrentCost(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>AI Professional Services (one-time)</label>
                  <span className="input-hint">Upfront PS cost paid in Month 1</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiPS}
                      min={0}
                      step={1}
                      placeholder="e.g. 75000"
                      onChange={(e) => setRoiPS(e.target.value)}
                    />
                  </div>
                </div>
                <div className="input-field">
                  <label>Expected AI Monthly Usage</label>
                  <span className="input-hint">Recurring monthly AI licensing &amp; usage cost (baseline estimate)</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyUsage}
                      min={0}
                      step={1}
                      placeholder="e.g. 10000"
                      onChange={(e) => setRoiMonthlyUsage(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Conservative Monthly Usage <span className="roi-optional-badge">optional</span></label>
                  <span className="input-hint">Higher monthly cost scenario — slower break-even</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyConservative}
                      min={0}
                      step={1}
                      placeholder="e.g. 15000"
                      onChange={(e) => setRoiMonthlyConservative(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Aggressive Monthly Usage <span className="roi-optional-badge">optional</span></label>
                  <span className="input-hint">Lower monthly cost scenario — faster break-even</span>
                  <div className="input-wrapper">
                    <span className="input-adornment prefix">$</span>
                    <input
                      type="number"
                      value={roiMonthlyAggressive}
                      min={0}
                      step={1}
                      placeholder="e.g. 6000"
                      onChange={(e) => setRoiMonthlyAggressive(e.target.value)}
                    />
                    <span className="input-adornment suffix">/mo</span>
                  </div>
                </div>
                <div className="input-field">
                  <label>Timeline (months)</label>
                  <span className="input-hint">Projection length — defaults to 36 if blank or 0</span>
                  <div className="input-wrapper">
                    <input
                      type="number"
                      value={roiTimeline}
                      min={1}
                      max={120}
                      step={1}
                      placeholder="36"
                      onChange={(e) => setRoiTimeline(e.target.value)}
                    />
                    <span className="input-adornment suffix">mo</span>
                  </div>
                </div>
              </div>

              {roiRows.length > 0 ? (() => {
                const hasConservative = roiRows.some(r => r.conservative !== null)
                const hasAggressive = roiRows.some(r => r.aggressive !== null)
                const anyBreakEven = roiRows.some(r =>
                  r.expected.netSavings >= 0 ||
                  (r.conservative && r.conservative.netSavings >= 0) ||
                  (r.aggressive && r.aggressive.netSavings >= 0)
                )
                const fmtUSD = (v) => `${v >= 0 ? '' : '-'}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                const fmtSavings = (v) => `${v >= 0 ? '+' : ''}${fmtUSD(v)}`
                return (
                  <div className="roi-table-wrap">
                    {!anyBreakEven && (
                      <div className="roi-no-breakeven">
                        No break-even reached within {roiRows.length} months at these inputs.
                      </div>
                    )}
                    <RoiLineChart rows={roiRows} />
                    <table className="roi-table">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Current</th>
                          {hasConservative && <th className="roi-th-conservative">Cons. Cost</th>}
                          {hasConservative && <th className="roi-th-conservative">Cons. Savings</th>}
                          <th className="roi-th-expected">Expected Cost</th>
                          <th className="roi-th-expected">Expected Savings</th>
                          {hasAggressive && <th className="roi-th-aggressive">Aggr. Cost</th>}
                          {hasAggressive && <th className="roi-th-aggressive">Aggr. Savings</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {roiRows.map((row) => {
                          const anyPositive = row.expected.netSavings >= 0 || (row.conservative?.netSavings >= 0) || (row.aggressive?.netSavings >= 0)
                          const rowClass = row.conservative?.isBreakEven ? 'roi-row-be-conservative'
                            : row.expected.isBreakEven ? 'roi-row-be-expected'
                            : row.aggressive?.isBreakEven ? 'roi-row-be-aggressive'
                            : anyPositive ? 'roi-row-positive' : 'roi-row-negative'
                          return (
                            <tr key={row.month} className={rowClass}>
                              <td className="roi-month-cell">{row.month}</td>
                              <td>{fmtUSD(row.cumulativeCurrent)}</td>
                              {hasConservative && <td>{row.conservative ? fmtUSD(row.conservative.cumulativeAI) : '—'}</td>}
                              {hasConservative && <td className={row.conservative ? (row.conservative.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative') : ''}>
                                {row.conservative ? fmtSavings(row.conservative.netSavings) : '—'}
                              </td>}
                              <td>{fmtUSD(row.expected.cumulativeAI)}</td>
                              <td className={row.expected.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative'}>
                                {fmtSavings(row.expected.netSavings)}
                              </td>
                              {hasAggressive && <td>{row.aggressive ? fmtUSD(row.aggressive.cumulativeAI) : '—'}</td>}
                              {hasAggressive && <td className={row.aggressive ? (row.aggressive.netSavings >= 0 ? 'roi-savings-positive' : 'roi-savings-negative') : ''}>
                                {row.aggressive ? fmtSavings(row.aggressive.netSavings) : '—'}
                              </td>}
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )
              })() : (
                <div className="placeholder-msg" style={{ padding: '20px 0' }}>
                  Enter your current monthly cost to generate the break-even table.
                </div>
              )}
            </section>
          </main>
  )
}

function AiCalculatorApp({ onBackToPlatformSelector, onBackToHome }) {
  const [calcTab, setCalcTab] = useState('agent')
  // Shared across both calculator tabs — same real-world negotiated Genesys
  // $/token rate and org-wide Named/Concurrent license type regardless of tab.
  const [licenseType, setLicenseType] = useState('named')
  const [genesysPriceOverride, setGenesysPriceOverride] = useState('')
  const [agentLines, setAgentLines] = useState({ cartLines: [], lineResults: {} })
  const [assistantsLines, setAssistantsLines] = useState({ cartLines: [], lineResults: {} })

  // Combine both tabs' lines into one namespaced flat list/result-map so
  // buildCartSummary() can apply the Genesys free-token discount exactly once,
  // across every Genesys line from both tabs — not per-tab (see CalculatorTab's
  // own showDiscount={false} CartSummary for why this moved up here).
  const combinedLines = useMemo(() => {
    const cartLines = [
      ...agentLines.cartLines.map((l) => ({ ...l, id: `agent:${l.id}` })),
      ...assistantsLines.cartLines.map((l) => ({ ...l, id: `assistants:${l.id}` })),
    ]
    const lineResults = {}
    agentLines.cartLines.forEach((l) => {
      if (agentLines.lineResults[l.id]) lineResults[`agent:${l.id}`] = agentLines.lineResults[l.id]
    })
    assistantsLines.cartLines.forEach((l) => {
      if (assistantsLines.lineResults[l.id]) lineResults[`assistants:${l.id}`] = assistantsLines.lineResults[l.id]
    })
    return { cartLines, lineResults }
  }, [agentLines, assistantsLines])
  const [roiCurrentCost, setRoiCurrentCost] = useState('')
  const [roiPS, setRoiPS] = useState('')
  const [roiMonthlyUsage, setRoiMonthlyUsage] = useState('')
  const [roiMonthlyConservative, setRoiMonthlyConservative] = useState('')
  const [roiMonthlyAggressive, setRoiMonthlyAggressive] = useState('')
  const [roiTimeline, setRoiTimeline] = useState('36')

  const roiRows = useMemo(() => {
    const currentCost = Number(roiCurrentCost)
    const ps = Number(roiPS)
    const expectedMonthly = Number(roiMonthlyUsage)
    const conservativeMonthly = roiMonthlyConservative !== '' ? Number(roiMonthlyConservative) : null
    const aggressiveMonthly = roiMonthlyAggressive !== '' ? Number(roiMonthlyAggressive) : null
    const months = roiTimeline === '' || Number(roiTimeline) === 0 ? 36 : Math.min(Math.max(Math.floor(Number(roiTimeline)), 1), 120)
    if (currentCost <= 0) return []
    let bkExpected = false, bkConservative = false, bkAggressive = false
    return Array.from({ length: months }, (_, i) => {
      const month = i + 1
      const cumulativeCurrent = currentCost * month
      const cumExpected = ps + expectedMonthly * month
      const netExpected = cumulativeCurrent - cumExpected
      const isBkExpected = !bkExpected && netExpected >= 0
      if (isBkExpected) bkExpected = true
      let conservative = null
      if (conservativeMonthly !== null) {
        const cumAI = ps + conservativeMonthly * month
        const net = cumulativeCurrent - cumAI
        const isBK = !bkConservative && net >= 0
        if (isBK) bkConservative = true
        conservative = { cumulativeAI: cumAI, netSavings: net, isBreakEven: isBK }
      }
      let aggressive = null
      if (aggressiveMonthly !== null) {
        const cumAI = ps + aggressiveMonthly * month
        const net = cumulativeCurrent - cumAI
        const isBK = !bkAggressive && net >= 0
        if (isBK) bkAggressive = true
        aggressive = { cumulativeAI: cumAI, netSavings: net, isBreakEven: isBK }
      }
      return { month, cumulativeCurrent, expected: { cumulativeAI: cumExpected, netSavings: netExpected, isBreakEven: isBkExpected }, conservative, aggressive }
    })
  }, [roiCurrentCost, roiPS, roiMonthlyUsage, roiMonthlyConservative, roiMonthlyAggressive, roiTimeline])

  return (
    <Box style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div className="ai-calculator-root">
        <nav className="calc-back-nav">
          {/* Top-left navigation: exit to platform selector + return to the SA Tools hub */}
          <PlatformNavBar
            onBackToPlatformSelector={onBackToPlatformSelector}
            onBackToHome={onBackToHome}
            showHome={!!onBackToHome}
            homeLabel="SA Tools"
            color="purple"
            mb={0}
          />
        </nav>

        <header className="app-header">
          <div className="header-inner">
            <div className="header-title">
              <div className="brand-name">Presidio</div>
              <h1>AI Calculator</h1>
              <p>Estimate AI licensing costs across Agent and Assistant solutions</p>
            </div>
          </div>
        </header>

        <div className="calc-tab-bar">
          <button className={`calc-tab${calcTab === 'agent' ? ' active' : ''}`} onClick={() => setCalcTab('agent')}>
            AI Agent Calculator
          </button>
          <button className={`calc-tab${calcTab === 'assistants' ? ' active' : ''}`} onClick={() => setCalcTab('assistants')}>
            AI Assistants Calculator
          </button>
          <button className={`calc-tab${calcTab === 'summary' ? ' active' : ''}`} onClick={() => setCalcTab('summary')}>
            AI Cost Summary
          </button>
          <button className={`calc-tab${calcTab === 'roi' ? ' active' : ''}`} onClick={() => setCalcTab('roi')}>
            ROI Break-Even
          </button>
        </div>

        <div style={{ display: calcTab === 'agent' ? 'block' : 'none' }}>
          <CalculatorTab
            solutionOrder={AGENT_SOLUTION_ORDER} defaultSelectedIds={[]}
            licenseType={licenseType} setLicenseType={setLicenseType}
            genesysPriceOverride={genesysPriceOverride} setGenesysPriceOverride={setGenesysPriceOverride}
            onLinesChange={setAgentLines}
          />
        </div>
        <div style={{ display: calcTab === 'assistants' ? 'block' : 'none' }}>
          <CalculatorTab
            solutionOrder={ASSISTANT_SOLUTION_ORDER} defaultSelectedIds={[]}
            licenseType={licenseType} setLicenseType={setLicenseType}
            genesysPriceOverride={genesysPriceOverride} setGenesysPriceOverride={setGenesysPriceOverride}
            onLinesChange={setAssistantsLines}
            lobMode
          />
        </div>

        {calcTab === 'summary' && (
          <CostSummaryTab combinedLines={combinedLines} licenseType={licenseType} />
        )}

        {calcTab === 'roi' && (
          <RoiTab
            roiRows={roiRows}
            roiCurrentCost={roiCurrentCost} setRoiCurrentCost={setRoiCurrentCost}
            roiPS={roiPS} setRoiPS={setRoiPS}
            roiMonthlyUsage={roiMonthlyUsage} setRoiMonthlyUsage={setRoiMonthlyUsage}
            roiMonthlyConservative={roiMonthlyConservative} setRoiMonthlyConservative={setRoiMonthlyConservative}
            roiMonthlyAggressive={roiMonthlyAggressive} setRoiMonthlyAggressive={setRoiMonthlyAggressive}
            roiTimeline={roiTimeline} setRoiTimeline={setRoiTimeline}
          />
        )}
      </div>
      <Footer platformColor="purple" />
    </Box>
  )
}

export default AiCalculatorApp;
